import {useRef} from 'react';

import {Alert, SimpleGrid} from '@mantine/core';
import {I18n} from '@aws-amplify/core';

import {ProviderMappingsContext, useProviderMappings} from '../hooks/useProviderMappings';
import {useProviderMappingsSelection} from '../hooks/useProviderMappingsSelection';
import {useProviderMappingsTableColumns} from '../hooks/useProviderMappingsTableColumns';
import {ProviderMapping} from '../types';

import {
    MantineDataTable,
    TableSearchForm,
    DataTableTokenPagination,
} from '@flexinet/ui-components';

// The message for an empty table. THREE different situations (§3, §13), and they must
// not read alike: nothing matches the search, the filter hides everything loaded, or
// there are no mappings at all. The API filters AFTER its Limit, so a filtered page can
// be empty while later pages still hold matches: say so when a cursor remains.
const emptyState = (term: string, filters: Record<string, string>, moreToLoad: boolean) => {
    const more = moreToLoad
        ? ` ${I18n.get('More provider mappings may be on a page that has not been loaded yet. Use Load More.')}`
        : '';
    if (term) {
        return {
            color: 'gray',
            title: I18n.get('No matching provider mappings'),
            body: `${I18n.get('Nothing in the loaded provider mappings matches')} "${term}". ${I18n.get('Clear the search to see every loaded row.')}${more}`,
        };
    }
    if (Object.keys(filters).length > 0) {
        return {
            color: 'gray',
            title: I18n.get('No provider mappings match the filter'),
            body: `${I18n.get('The filter')} (${Object.entries(filters)
                                                         .map(([key, value]) => `${key}: ${value}`)
                                                         .join(', ')}) ${I18n.get('hides every loaded provider mapping.')}${more}`,
        };
    }
    return {
        color: 'blue',
        title: I18n.get('No provider mappings yet'),
        body: I18n.get('A provider mapping links a customer, product or price to its record in a billing provider such as Stripe. Use Add Mapping to create the first one.'),
    };
};

// Rendered on the index page and, with one of the ids set, inside the product, customer
// and price profile tabs. Every instance keeps its Load More footer: an embedded list is
// filtered after the API's Limit and can need a second page to show anything.
export const RecordsTable = (context: ProviderMappingsContext) => {
    const {selectedRecords, setSelectedRecords} = useProviderMappingsSelection();

    const {
        items,
        visibleItems,
        keyword,
        filters,
        sortStatus,
        setSortStatus,
        loading,
        fetching,
        isError,
        isFetchingMore,
        nextToken,
        fetchMoreRecords,
        handleFilterByKeyword
    } = useProviderMappings(context);

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    const {columns} = useProviderMappingsTableColumns();

    const term = keyword.trim();
    const empty = emptyState(term, filters, nextToken !== null);

    return (
        <SimpleGrid verticalSpacing='xl' m='sm'>
            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>

            {isError && (
                <Alert color='red' title={I18n.get('Could not load provider mappings')}>
                    {I18n.get('The billing API did not return the provider mappings list. Try Reload; if it persists, the API may be unavailable in this environment.')}
                </Alert>
            )}

            <MantineDataTable<ProviderMapping>
                columns={columns}
                records={visibleItems}
                // Sorting is client-side over the pages loaded so far, the same scope as
                // the keyword filter. Token pagination cannot reorder unseen rows.
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                selectedRecords={selectedRecords}
                onSelectedRecordsChange={setSelectedRecords}
                fetching={loading || fetching}
                onScrollToBottom={() => fetchMoreRecords()}
                scrollViewportRef={scrollViewportRef}
                withTableBorder={true}
            />

            {/*
              Gated on !loading so it does not flash during the first fetch, and on !isError
              so a failed load keeps its own Alert rather than claiming there are none.
            */}
            {!loading && !isError && visibleItems.length === 0 && (
                <Alert color={empty.color} title={empty.title}>
                    {empty.body}
                </Alert>
            )}

            <DataTableTokenPagination
                loadedCount={visibleItems.length}
                unfilteredCount={items.length}
                filterTerm={keyword}
                noun={'provider mapping'}
                nextToken={nextToken}
                isFetchingMore={isFetchingMore}
                handleLoadMore={fetchMoreRecords}
            />
        </SimpleGrid>
    );
};
